// What a Canvas share will do, in the person's words: whom it names, what they may do and until when. One set of words
// for every screen that asks the person to decide a share (Can 47): the Desk's Inbox card (apps/web gateInstancesClient.ts
// plainCanvasShare, W-f9a102e0, W-7cdf5337) and Pocket's card in the conversation (apps/mobile cloudConversationGate.ts).
// Pure: it reads nothing; the names it is given were read by the screen from the World.

/** A member or group a share names, by name, as the World reads it now. */
export interface ShareRecipientName {
  readonly name: string;
  readonly memberCount?: number | null;
}

/** Whom a share names, by id, so a screen can read the name. */
export type ShareRecipientRef =
  | { readonly kind: "group"; readonly groupId: string }
  | { readonly kind: "actor"; readonly actorId: string };

export const SHARE_RIGHTS: Readonly<Record<string, string>> = {
  read: "open and read it",
  edit: "change it",
  share: "share it with others",
  publish: "publish it on a website",
};

/** Words as a sentence: a capital first letter and a full stop. */
export const sentence = (words: string): string => `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;

/** Items as a person reads them: "A", "A and B", "A, B and C". */
export const joinedWords = (items: ReadonlyArray<string>): string =>
  items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;

/** A group's size as a person reads it: "1 member", "3 members". */
const members = (count: number): string => `${count} ${count === 1 ? "member" : "members"}`;

/**
 * Who a Canvas share names (backend lib/canvasRequest.js itemAudience): exactly one of audience "world-members", a
 * group or a member. None, more than one, or an audience not known here is not described (#1806): the person reads
 * the exact request instead. W-7cdf5337: the person or group by name, once read (recipient); until then, and when it
 * cannot be read, the words stay general.
 */
export function shareAudienceOf(
  input: Record<string, unknown>,
  recipient: ShareRecipientName | null,
): {
  readonly headline: string;
  readonly with: string;
  readonly shareRecipient: ShareRecipientRef | null;
} | null {
  const named = ["audience", "groupId", "recipientActorId"].filter((key) => key in input);
  if (named.length !== 1) return null;
  const groupId = typeof input["groupId"] === "string" ? input["groupId"] : "";
  const actorId = typeof input["recipientActorId"] === "string" ? input["recipientActorId"] : "";
  if (named[0] === "audience")
    return input["audience"] === "world-members"
      ? {
          headline: "everyone in this World",
          with: "Everyone in this World.",
          shareRecipient: null,
        }
      : null;
  if (named[0] === "groupId" && groupId !== "")
    return {
      ...(recipient
        ? {
            headline: `the group “${recipient.name}” in this World`,
            with: `The members of the group “${recipient.name}” in this World${typeof recipient.memberCount === "number" ? ` (${members(recipient.memberCount)} now)` : ""}. Whoever is in the group has this access while they are in it.`,
          }
        : {
            headline: "the members of one group in this World",
            with: "The members of one group in this World.",
          }),
      shareRecipient: { kind: "group", groupId },
    };
  if (named[0] === "recipientActorId" && actorId !== "")
    return {
      ...(recipient
        ? { headline: recipient.name, with: `${recipient.name}, a member of this World.` }
        : { headline: "one member of this World", with: "One member of this World." }),
      shareRecipient: { kind: "actor", actorId },
    };
  return null;
}

/** The rights a share gives, in words; null when any is one not named here (it is not guessed at). */
export function shareRightsOf(actions: ReadonlyArray<unknown>): string | null {
  if (actions.length === 0) return null;
  const rights = actions.map((action) =>
    typeof action === "string" && Object.hasOwn(SHARE_RIGHTS, action) ? SHARE_RIGHTS[action]! : "",
  );
  return rights.some((right) => right === "") ? null : joinedWords(rights);
}

/** Until when a share lasts; null when it names an end that is not a time. */
export function shareUntil(input: Record<string, unknown>): string | null {
  const expiresAt = input["expiresAt"];
  if (expiresAt === undefined) return "Until you withdraw it. The share has no end date.";
  return typeof expiresAt === "number" && Number.isFinite(expiresAt)
    ? `${new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(expiresAt)}, then it ends by itself.`
    : null;
}

// W-7cdf5337: what sharing a Canvas with its data gives, one sentence per Component, collection and snapshot, read from the
// canvas-dependencies preview the World issues the grants from (the Desk's Share dialog, its Inbox card and Pocket's card).
/** One entry of a canvas-dependencies preview, as the World answers it. */
export interface CanvasShareDependency {
  readonly kind: string;
  readonly id: string;
  readonly name: string;
  readonly version?: number;
  readonly actions: ReadonlyArray<string>;
  readonly rowScope?: string;
}

/** Which rows of a collection the access covers. */
export const collectionScope = (rowScope?: string): string =>
  rowScope === "actor"
    ? "recipient’s own rows"
    : rowScope === "collection"
      ? "whole collection"
      : "unavailable";

/** What one collection's access lets its recipient do: read (every row, or their own), now and later, outside this Canvas too; and change, when given. */
export function collectionAccessWords(
  rowScope: string | undefined,
  actions: ReadonlyArray<string>,
): string {
  if (rowScope !== "actor" && rowScope !== "collection")
    return "this collection is unavailable, so sharing it is refused.";
  const own = rowScope === "actor";
  const read = `they can read ${own ? "every field of their own rows" : "every row and field"}, now and later, also outside this Canvas, for as long as this Canvas share lasts.`;
  return actions.includes("edit")
    ? `${read} They can also add, change and delete ${own ? "their own rows" : "any row"}.`
    : read;
}

/** One dependency a share gives, by name, and what the recipient can do with it. */
export function canvasShareDependencyWords(item: CanvasShareDependency): string {
  const label = `${item.name}${item.version ? ` · version ${item.version}` : ""}`;
  if (item.kind === "collection")
    return `${label} · ${collectionScope(item.rowScope)}: ${collectionAccessWords(item.rowScope, item.actions)}`;
  if (item.kind === "component")
    return `${label} · ${item.actions.join(", ")}: they can use this exact version, not read its source.`;
  if (item.kind === "snapshot")
    return `${label} · ${item.actions.join(", ")}: they can read this fixed snapshot.`;
  return `${label}: it cannot be shared with a live Canvas; share a fixed copy instead.`;
}

/**
 * What else a share gives, as its "Includes" row says it: nothing else, or each Component, collection and snapshot of the World's
 * list for that revision (W-7cdf5337), in the words above.
 */
export function canvasShareIncludes(sequence: number, listed: ReadonlyArray<CanvasShareDependency>): string {
  return listed.length === 0
    ? `Nothing else: revision ${sequence} of this Canvas uses no Components, collections or snapshots.`
    : [
        `From revision ${sequence}, as the World lists it now. This access lasts as long as this Canvas share, whatever later revisions use.${listed.some((item) => item.kind === "collection") ? "" : " No collection access is given; live data declared for this Canvas's readers reaches them through its datasets."}`,
        ...listed.map((item) => `• ${canvasShareDependencyWords(item)}`),
      ].join("\n");
}

/** The World's list of what a share of one revision gives, and its digest (the one a share call names as dependencyDigest). */
export interface CanvasShareData {
  readonly sequence: number;
  readonly digest: string;
  readonly dependencies: ReadonlyArray<CanvasShareDependency>;
}

/** The Canvas a share names, as a person reads it. */
export const sharedCanvasWords = (canvasName: string | null): string =>
  canvasName ? `the Canvas “${canvasName}”` : "a Canvas";

/**
 * A Canvas share in words: its headline and its What, With, They may, Includes and Until rows. A share with no dependencyDigest gives
 * nothing but the Canvas (no Includes row); one with it is said only with the World's list for the revision it names (shareData, its
 * digest the call's), in the Includes row. Null when the call is not such a share, or names anything these words do not describe
 * exactly; the screen then shows the exact request.
 */
export function plainCanvasShareOnly(
  input: Record<string, unknown>,
  names: {
    readonly canvasName: string | null;
    readonly recipient: ShareRecipientName | null;
    readonly shareData?: CanvasShareData | null;
  },
): {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
  readonly shareRecipient: ShareRecipientRef | null;
} | null {
  if (input["action"] !== undefined && input["action"] !== "canvas-share") return null;
  if (typeof input["canvasId"] !== "string") return null;
  const actions = Array.isArray(input["actions"]) ? input["actions"] : null;
  if (actions === null || actions.length === 0) return null;
  // With its dependency review: only the World's list for exactly that revision and digest says what else it gives. Data changes go
  // only with a share that can edit (W-d2731d4e: the World refuses them otherwise).
  const digest = input["dependencyDigest"];
  const data = names.shareData ?? null;
  if (digest === undefined ? input["dataChanges"] === true : typeof digest !== "string" || data === null || data.digest !== digest || data.sequence !== input["sequence"] || (input["dataChanges"] === true && !actions.includes("edit")))
    return null;
  const audience = shareAudienceOf(input, names.recipient);
  const rights = shareRightsOf(actions);
  const until = shareUntil(input);
  if (audience === null || rights === null || until === null) return null;
  const what = sharedCanvasWords(names.canvasName);
  return {
    headline: `Share ${what} with ${audience.headline}`,
    rows: [
      ["What", sentence(`${what}, in every version, including later ones`)],
      ["With", audience.with],
      ["They may", sentence(rights)],
      ...(digest === undefined || data === null ? [] : [["Includes", canvasShareIncludes(data.sequence, data.dependencies)] as const]),
      ["Until", until],
    ],
    shareRecipient: audience.shareRecipient,
  };
}
